import { useEffect, useRef } from "react";

// Cinematic golden particle spiral (phyllotaxis / golden-angle),
// slowly rotating, brighter at the core, faded at the edges.
export function SpiralCinematic({ size = 260, className = "", particles = 520 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const maxR = size * 0.46;
    const GOLDEN = Math.PI * (3 - Math.sqrt(5)); // ~2.399963

    let t = 0;
    let start = performance.now();

    const draw = (now) => {
      const elapsed = (now - start) / 1000;
      const intro = Math.min(elapsed / 1.2, 1); // 0->1 reveal
      t += 0.0025;
      ctx.clearRect(0, 0, size, size);
      ctx.globalCompositeOperation = "lighter";

      for (let i = 0; i < particles; i++) {
        const norm = i / particles;
        const angle = i * GOLDEN + t * (1.4 - norm); // inner spins faster
        const r = maxR * Math.sqrt(norm) * intro;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;

        // fade toward the edge ("fondu") + gentle twinkle
        const edgeFade = 1 - Math.pow(norm, 1.6);
        const twinkle = 0.7 + 0.3 * Math.sin(t * 6 + i * 0.5);
        const alpha = edgeFade * twinkle * intro;

        // color: gold core -> amber -> deep orange edge
        const hue = 46 - norm * 22;
        const light = 72 - norm * 22;
        const dotR = (1.6 - norm) * 1.7 + 0.3;

        ctx.beginPath();
        ctx.fillStyle = `hsla(${hue}, 95%, ${light}%, ${alpha})`;
        ctx.shadowBlur = 8 * edgeFade;
        ctx.shadowColor = `hsla(${hue}, 95%, 60%, ${alpha})`;
        ctx.arc(x, y, dotR, 0, Math.PI * 2);
        ctx.fill();
      }

      // warm core glow
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR * 0.8);
      g.addColorStop(0, `hsla(45, 100%, 70%, ${0.28 * intro})`);
      g.addColorStop(1, "transparent");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, size, size);

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [size, particles]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width: size,
        height: size,
        WebkitMaskImage: "radial-gradient(circle, #000 55%, transparent 78%)",
        maskImage: "radial-gradient(circle, #000 55%, transparent 78%)",
      }}
      aria-hidden
    />
  );
}
